import type { ConsumerApiClient } from '../client';

export interface WalletBalance {
  balanceCents: number;
  autoReloadEnabled: boolean;
  autoReloadAmountCents: number;
  autoReloadThresholdCents: number;
}

export interface WalletTransaction {
  id: string;
  type: 'credit' | 'debit';
  amountCents: number;
  description: string;
  createdAt: string;
  balanceAfterCents: number;
}

export interface AddFundsRequest {
  amountCents: number;
  paymentMethodId?: string;
}

export interface AutoReloadConfig {
  enabled: boolean;
  amountCents: number;
  thresholdCents: number;
}

export interface WalletTransactionsResponse {
  transactions: WalletTransaction[];
  total: number;
}

export interface WalletApi {
  getBalance(): Promise<WalletBalance>;
  getTransactions(page?: number): Promise<WalletTransactionsResponse>;
  addFunds(data: AddFundsRequest): Promise<any>;
  updateAutoReload(config: AutoReloadConfig): Promise<WalletBalance>;
}

export function createWalletApi(client: ConsumerApiClient): WalletApi {
  return {
    async getBalance() {
      const data = await client.get<any>('/wallet');
      return {
        balanceCents: data.balanceCents || 0,
        autoReloadEnabled: data.autoReload?.isEnabled || false,
        autoReloadAmountCents: data.autoReload?.reloadAmountCents || 2500,
        autoReloadThresholdCents: data.autoReload?.triggerBalanceCents || 500,
      };
    },

    async getTransactions(_page = 1) {
      const data = await client.get<any>('/wallet/transactions');
      const txs: WalletTransaction[] = (data.transactions || []).map((tx: any) => ({
        id: tx.id,
        type: tx.type === 'add_funds' ? ('credit' as const) : ('debit' as const),
        amountCents: tx.amountCents,
        description: tx.description || tx.type,
        createdAt: tx.createdAt,
        balanceAfterCents: tx.balanceAfterCents,
      }));
      return { transactions: txs, total: txs.length };
    },

    addFunds(data) {
      return client.post('/wallet/add-funds', data);
    },

    async updateAutoReload(config) {
      const data = await client.put<any>('/wallet/auto-reload', {
        isEnabled: config.enabled,
        reloadAmountCents: config.amountCents,
        triggerBalanceCents: config.thresholdCents,
      });
      return {
        balanceCents: data.balanceCents ?? data.balance_cents ?? 0,
        autoReloadEnabled: data.isEnabled ?? data.autoReloadEnabled ?? false,
        autoReloadAmountCents: data.reloadAmountCents ?? data.autoReloadAmountCents ?? 0,
        autoReloadThresholdCents: data.triggerBalanceCents ?? data.autoReloadThresholdCents ?? 0,
      };
    },
  };
}
